import axios from "axios";
import { getCurrentUser, saveUserToStorage } from "./userStorage";
import { normalizeSubscriptionAccessFromApi } from "./subscriptionAccess";

const API_URL =
  process.env.REACT_APP_Environment === "development"
    ? process.env.REACT_APP_API_URL
    : process.env.REACT_APP_API_URL_Production;

/** In-flight refresh promise so concurrent callers share one request */
let refreshPromise = null;

const getKeepMeLoginPreference = (user) => {
  if (user?.keepMeLogin) return true;
  try {
    const stored = localStorage.getItem("keepMeLogin");
    return stored ? JSON.parse(stored) : false;
  } catch {
    return false;
  }
};

/**
 * Renew the access JWT via GET /api/Auth/RefreshToken?token=<accessJwt>.
 * Backend validates the JWT with lifetime checks disabled, so expired tokens can still refresh.
 * @returns {Promise<{ token: string, user: object, subscriptionAccess: object|null }>}
 */
export const refreshAccessToken = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const user = getCurrentUser();
    if (!user?.token) {
      throw new Error("No access token available to refresh");
    }

    const refreshResponse = await axios.get(`${API_URL}/api/Auth/RefreshToken`, {
      params: { token: user.token },
      // Do not use axiosInstance — avoid interceptor recursion
      validateStatus: (status) => status < 500,
    });

    const payload = refreshResponse.data;
    if (refreshResponse.status !== 200 || !payload?.status) {
      throw new Error(payload?.message || "Token refresh failed");
    }

    const data = payload.data || {};
    const newToken = data.token || data.Token;
    if (!newToken) {
      throw new Error("Token refresh response did not include a token");
    }

    const refreshedSubscription = normalizeSubscriptionAccessFromApi(
      data.subscriptionAccess ?? data.SubscriptionAccess
    );
    const keepMeLogin = getKeepMeLoginPreference(user);

    const updatedUser = {
      ...user,
      token: newToken,
      keepMeLogin,
      ...(refreshedSubscription
        ? {
            subscriptionAccess: refreshedSubscription,
            subscriptionAccessFromApi: refreshedSubscription,
            subscriptionRestricted: refreshedSubscription.isRestricted,
          }
        : {}),
    };

    saveUserToStorage(updatedUser);

    window.dispatchEvent(
      new CustomEvent("tokenRefreshed", {
        detail: {
          token: newToken,
          keepMeLogin,
          subscriptionAccess: refreshedSubscription,
        },
      })
    );

    return {
      token: newToken,
      user: updatedUser,
      subscriptionAccess: refreshedSubscription,
    };
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
};

export default refreshAccessToken;
